/*
    USER PREFERENCE KEYS

    Central registry of every user preference key the app reads or writes, so keys can be
    looked up in one place rather than being spelled out as string literals at each call site.

    Preferences are stored server side, one key/value row per user, via:

        itBnTx.getUserPref({ inPrefKey : <key>, inDefaultValue : <value> })   // GET    /rest/user/pref
        itBnTx.updateUserPref({ inPrefKey : <key>, inPrefValue : <value> })   // PUT    /rest/user/pref
        itBnTx.createUserPref({ inPrefKey : <key>, inPrefValue : <value> })   // POST   /rest/user/pref
        itBnTx.deleteUserPref({ inPrefKey : <key> })                          // DELETE /rest/user/pref

    NOTE: getUserPref with an inDefaultValue will create the key server side with that default
          and return it, when the key does not yet exist. So reading with the `defaultValue`
          below is all that is needed to get the documented default for a user who has never
          set the preference.

    NOTE: all preference values are stored as strings. Booleans are stored as "0" / "1" - use
          the prefValueToBool() / boolToPrefValue() helpers at the bottom of this file.

    NOTE: the guided help "already seen this screen" flags also live in user prefs, but their
          keys are built dynamically from the UI context help ID (CONFIG.USER_PREF_PREFIX +
          helpIDToShow, see components/bntx-home), so they cannot be listed statically here.
*/

const {CONFIG} = require('@/js/bntx-config')


/*
    The default texts name the marker shape in words AND show the glyph, e.g. "(triangle ▲)".
    The report is rendered to PDF by the backend, and some fonts have no glyph for these
    characters and print a box instead - the word means the reader still knows what is meant.

    The glyphs are chosen for font coverage where possible: ▲ (U+25B2) and ▼ (U+25BC) are in
    practically every font. The star ✶ (U+2736) matches the six pointed marker artwork but is
    less widely supported, which is exactly why the word "star" sits beside it.

    NOTE: getUserPref creates the key server side on first read, so changing a default here only
          reaches users whose key does not exist yet. Existing users keep their stored text.
*/
export const DEFAULT_EMG_COMPLIANCE_TEXT         = "As clinically indicated, all muscles were injected under EMG guidance (triangle ▲) with good traces obtained to accurately localise the muscle and ensure patient safety.";
export const DEFAULT_ULTRASOUND_COMPLIANCE_TEXT  = "As clinically indicated, all muscles were injected under Ultrasound guidance (inverted triangle ▼) with good visualisation obtained to accurately localise the muscle and ensure patient safety.";
export const DEFAULT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT = "As clinically indicated, all muscles were injected under combined EMG and Ultrasound guidance (star ✶) with good traces and visualisation obtained to accurately localise the muscle and ensure patient safety.";


export const USER_PREF = {

    ////////////////////
    // REPORT
    ////////////////////

    REPORT_AUTO_INCLUDE_EMG_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.auto-include-emg-compliance-text",
        defaultValue : "1",
        description  : "Auto append the EMG compliance text when EMG injections are detected in a session. Value is 0|1, drives the checkbox state."
    },

    REPORT_AUTO_INCLUDE_ULTRASOUND_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.auto-include-ultrasound-compliance-text",
        defaultValue : "1",
        description  : "Auto append the Ultrasound compliance text when ultrasound injections are detected in a session. Value is 0|1, drives the checkbox state."
    },

    REPORT_EMG_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.emg-compliance-text",
        defaultValue : DEFAULT_EMG_COMPLIANCE_TEXT,
        description  : "EMG default compliance text. Value is a text string."
    },

    REPORT_ULTRASOUND_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.ultrasound-compliance-text",
        defaultValue : DEFAULT_ULTRASOUND_COMPLIANCE_TEXT,
        description  : "Ultrasound default compliance text. Value is a text string."
    },

    REPORT_AUTO_INCLUDE_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.auto-include-emg-and-ultrasound-compliance-text",
        defaultValue : "1",
        description  : "Auto append the combined EMG and Ultrasound compliance text when an injection used both EMG and ultrasound guidance together. Value is 0|1, drives the checkbox state."
    },

    REPORT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT : {
        key          : CONFIG.USER_PREF_PREFIX + "report.emg-and-ultrasound-compliance-text",
        defaultValue : DEFAULT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT,
        description  : "Combined EMG and Ultrasound default compliance text, which also names the star marker. Value is a text string."
    },
}


/*
    Preference values are always stored as strings, these two helpers keep the
    "0" / "1" <-> boolean conversion in the one place.
*/
export function prefValueToBool (inPrefValue) {
    return inPrefValue == "1"
}

export function boolToPrefValue (inValue) {
    return inValue ? "1" : "0"
}
